import { useShopStore } from '../store/useShopStore';
import { getAsset } from './Palette';

export default function Inspector({ collapsed, onToggle }) {
  const {
    selectedId,
    layout,
    machineStatus,
    updateEntityMeta,
    deleteEntity,
  } = useShopStore();

  const entity = selectedId 
    ? layout.entities.find(e => e.id === selectedId) 
    : null;

  const asset = entity ? getAsset(entity.assetKey) : null;
  const status = entity?.meta?.machineId 
    ? machineStatus[entity.meta.machineId] 
    : null;

  // Collapsed state
  if (collapsed) {
    return (
      <div className="w-10 h-full bg-gray-800/95 backdrop-blur-sm border-l border-gray-700 flex flex-col items-center py-2">
        <button
          onClick={onToggle}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          title="Show Inspector"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {entity && (
          <div 
            className="w-6 h-6 rounded mt-2"
            style={{ backgroundColor: `#${(asset?.color || 0x666666).toString(16).padStart(6, '0')}` }}
            title={asset?.name || 'Selected entity'}
          />
        )}
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="w-64 h-full bg-gray-800/95 backdrop-blur-sm border-l border-gray-700 flex flex-col">
        <div className="p-3 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-300">Inspector</h2>
          <button
            onClick={onToggle}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Collapse"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-500">
            Select an entity to inspect
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 h-full bg-gray-800/95 backdrop-blur-sm border-l border-gray-700 flex flex-col">
      <div className="p-3 border-b border-gray-700 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-300">Inspector</h2>
        <button
          onClick={onToggle}
          className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          title="Collapse"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Asset Info */}
        <div>
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            Asset
          </h3>
          <div className="bg-gray-700/50 rounded p-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded"
                style={{ backgroundColor: `#${(asset?.color || 0x666666).toString(16).padStart(6, '0')}` }}
              />
              <div>
                <div className="font-medium text-white">{asset?.name || entity.assetKey}</div>
                <div className="text-xs text-gray-400">
                  {asset?.footprint?.w || 1}x{asset?.footprint?.h || 1} tiles
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Position */}
        <div>
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            Position
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Tile X</label>
              <input
                type="number"
                value={entity.tx}
                onChange={(e) => {
                  const tx = parseInt(e.target.value) || 0;
                  useShopStore.getState().updateEntityPos(entity.id, tx, entity.ty);
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Tile Y</label>
              <input
                type="number"
                value={entity.ty}
                onChange={(e) => {
                  const ty = parseInt(e.target.value) || 0;
                  useShopStore.getState().updateEntityPos(entity.id, entity.tx, ty);
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
              />
            </div>
          </div>
        </div>

        {/* Machine Binding */}
        <div>
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            Machine Binding
          </h3>
          <div>
            <label className="text-xs text-gray-500">Machine ID (from simulator)</label>
            <input
              type="text"
              value={entity.meta?.machineId || ''}
              onChange={(e) => updateEntityMeta(entity.id, { machineId: e.target.value })}
              placeholder="e.g., SPM-3000-1"
              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Link to a simulator machine for live status
            </p>
          </div>
        </div>

        {/* Label */}
        <div>
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            Display
          </h3>
          <div>
            <label className="text-xs text-gray-500">Label</label>
            <input
              type="text"
              value={entity.meta?.label || ''}
              onChange={(e) => updateEntityMeta(entity.id, { label: e.target.value })}
              placeholder="Custom label..."
              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm mt-1"
            />
          </div>
        </div>

        {/* Live Status (if bound to machine) */}
        {status && (
          <div>
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              Live Status
            </h3>
            <div className="bg-gray-700/50 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">State</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  status.execution === 'ACTIVE' 
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {status.execution}
                </span>
              </div>
              {status.program && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Program</span>
                  <span className="text-white text-sm">{status.program}</span>
                </div>
              )}
              {status.part_count !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Parts</span>
                  <span className="text-white text-sm">{status.part_count}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Entity ID (for debugging) */}
        <div className="pt-2 border-t border-gray-700">
          <div className="text-xs text-gray-500">
            ID: <code className="text-gray-400">{entity.id.slice(0, 8)}...</code>
          </div>
        </div>
      </div>

      {/* Delete button */}
      <div className="p-3 border-t border-gray-700">
        <button
          onClick={() => deleteEntity(entity.id)}
          className="w-full px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-sm rounded"
        >
          Delete Entity
        </button>
      </div>
    </div>
  );
}
